import React, { useEffect, useRef, useState } from "react";
import { onStatusChange } from "../../../utils/syncQueue";
import "./SaveStatusIndicator.css";

const SAVED_PILL_MS = 1800;

// Renders a small fixed-position pill that reflects the most recent
// syncQueue flush. Subscribers to `onStatusChange` receive every
// transition; this component only shows itself when status is non-idle
// and auto-hides "saved" after a short window so a quiet success
// doesn't leave UI noise. "error" persists until the next saving/saved
// because the queue retries silently and the user needs the signal.
//
// Optional `recordType` prop scopes the indicator to a single record
// type — useful when multiple syncQueue-driven screens could be
// mounted at the same time, though in practice the app only mounts one
// at a time.
export default function SaveStatusIndicator({ recordType }) {
  const [state, setState] = useState({ status: "idle" });
  const hideTimerRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onStatusChange((event) => {
      if (recordType && event.recordType !== recordType) return;

      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }

      setState(event);

      if (event.status === "saved") {
        hideTimerRef.current = setTimeout(() => {
          setState({ status: "idle" });
          hideTimerRef.current = null;
        }, SAVED_PILL_MS);
      }
    });

    return () => {
      unsubscribe();
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [recordType]);

  if (state.status === "idle") return null;

  let label;
  if (state.status === "saving") {
    label = "Saving…";
  } else if (state.status === "saved") {
    label = "Saved";
  } else {
    label = "Couldn’t save — retrying";
  }

  return (
    <div
      className={`nwd-save-status nwd-save-status--${state.status}`}
      role="status"
      aria-live="polite"
    >
      {label}
    </div>
  );
}
